import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainNavbar from './MainNavbar';

const GrupoView = () => {
  const [user, setUser] = useState(null);
  const [categoria, setCategoria] = useState(null);
  const [alumnos, setAlumnos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('lista');

  // Asistencias
  const [fechaLista, setFechaLista] = useState(new Date().toISOString().split('T')[0]);
  const [asistenciaMap, setAsistenciaMap] = useState({});
  const [fechasHistorial, setFechasHistorial] = useState([]);
  const [guardandoLista, setGuardandoLista] = useState(false);

  // Observaciones
  const [alumnoObs, setAlumnoObs] = useState(null);
  const [observaciones, setObservaciones] = useState([]);
  const [nuevaObs, setNuevaObs] = useState('');

  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('token');

  const fetchGrupo = useCallback(async (usuario) => {
    try {
      const token = getToken();
      const res = await fetch('/api/alumnos', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 401) {
        localStorage.removeItem('token');
        navigate('/admin');
        return;
      }
      const todosAlumnos = await res.json();

      const resCat = await fetch('/api/categorias');
      const todasCategorias = await resCat.json();
      setCategorias(todasCategorias);

      const miCategoria = todasCategorias.find(c => c.id === parseInt(usuario.categoria_id));
      setCategoria(miCategoria);

      const alumnosGrupo = usuario.role === 'admin'
        ? todosAlumnos.filter(a => !miCategoria || a.categoria_id === miCategoria.id)
        : todosAlumnos.filter(a => a.categoria_id === parseInt(usuario.categoria_id));

      setAlumnos(alumnosGrupo);
    } catch (error) {
      console.error('Error cargando grupo:', error);
      alert('Error cargando los datos del grupo');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = getToken();
    const userData = localStorage.getItem('user');
    if (!token || !userData) {
      navigate('/admin');
      return;
    }
    const usuario = JSON.parse(userData);
    if (usuario.role !== 'entrenador' && usuario.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    setUser(usuario);
    fetchGrupo(usuario);
  }, [navigate, fetchGrupo]);

  const fetchAsistenciasFecha = useCallback(async () => {
    if (!categoria) return;
    try {
      const token = getToken();
      const res = await fetch(`/api/asistencias?categoria_id=${categoria.id}&fecha=${fechaLista}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      const map = {};
      alumnos.forEach(a => { map[a.id] = true; });
      if (Array.isArray(data)) {
        data.forEach(reg => { map[reg.alumno_id] = reg.asistio === 1; });
      }
      setAsistenciaMap(map);
    } catch (error) {
      console.error('Error cargando asistencias:', error);
    }
  }, [categoria, fechaLista, alumnos]);

  const fetchHistorial = useCallback(async () => {
    if (!categoria) return;
    try {
      const token = getToken();
      const res = await fetch(`/api/asistencias/fechas?categoria_id=${categoria.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setFechasHistorial(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error cargando historial:', error);
    }
  }, [categoria]);

  useEffect(() => {
    if (categoria && alumnos.length >= 0) {
      fetchAsistenciasFecha();
      fetchHistorial();
    }
  }, [categoria, fechaLista, fetchAsistenciasFecha, fetchHistorial, alumnos.length]);

  const toggleAsistencia = (alumnoId) => {
    setAsistenciaMap(prev => ({ ...prev, [alumnoId]: !prev[alumnoId] }));
  };

  const guardarLista = async () => {
    setGuardandoLista(true);
    try {
      const token = getToken();
      const lista = alumnos.map(a => ({
        alumno_id: a.id,
        asistio: !!asistenciaMap[a.id]
      }));

      const res = await fetch('/api/asistencias', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          categoria_id: categoria.id,
          fecha: fechaLista,
          lista
        })
      });

      const result = await res.json();
      if (res.ok) {
        setMessage('✓ Lista guardada correctamente');
        fetchHistorial();
        setTimeout(() => setMessage(''), 3000);
      } else {
        alert(result.error || 'Error guardando la lista');
      }
    } catch (error) {
      alert('Error de conexión');
    } finally {
      setGuardandoLista(false);
    }
  };

  const abrirObservaciones = async (alumno) => {
    setAlumnoObs(alumno);
    setNuevaObs('');
    try {
      const token = getToken();
      const res = await fetch(`/api/alumnos/${alumno.id}/observaciones`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setObservaciones(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error cargando observaciones:', error);
    }
  };

  const guardarObservacion = async () => {
    if (!nuevaObs.trim()) return;
    try {
      const token = getToken();
      const res = await fetch(`/api/alumnos/${alumnoObs.id}/observaciones`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ texto: nuevaObs })
      });
      if (res.ok) {
        setNuevaObs('');
        abrirObservaciones(alumnoObs);
      } else {
        const result = await res.json();
        alert(result.error || 'Error guardando observación');
      }
    } catch (error) {
      alert('Error de conexión');
    }
  };

  const eliminarObservacion = async (obsId) => {
    if (!window.confirm('¿Eliminar esta observación?')) return;
    try {
      const token = getToken();
      const res = await fetch(`/api/observaciones/${obsId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setObservaciones(observaciones.filter(o => o.id !== obsId));
      }
    } catch (error) {
      console.error('Error eliminando observación:', error);
    }
  };

  const moverJugador = async (alumno, nuevaCategoriaId) => {
    if (!nuevaCategoriaId) return;
    const catDestino = categorias.find(c => c.id === parseInt(nuevaCategoriaId));
    if (!window.confirm(`¿Mover a ${alumno.nombre} ${alumno.apellidos} al grupo "${catDestino.nombre}"?`)) return;

    try {
      const token = getToken();
      const res = await fetch(`/api/alumnos/${alumno.id}/mover`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ categoria_id: parseInt(nuevaCategoriaId) })
      });

      const result = await res.json();
      if (res.ok) {
        setMessage(`✓ ${alumno.nombre} movido a ${catDestino.nombre}`);
        fetchGrupo(user);
        setTimeout(() => setMessage(''), 3000);
      } else {
        alert(result.error || 'Error moviendo jugador');
      }
    } catch (error) {
      alert('Error de conexión');
    }
  };

  const formatFecha = (f) => new Date(f + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <MainNavbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <MainNavbar />
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Cabecera del grupo */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-4">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            Mi Grupo {categoria ? `- ${categoria.nombre}` : ''}
          </h1>
          {categoria && (
            <p className="text-sm text-gray-600 mt-1">
              {categoria.dias_entreno} · {categoria.hora_inicio} - {categoria.hora_fin} · {alumnos.length} niños
            </p>
          )}
          {!categoria && user?.role === 'admin' && (
            <p className="text-sm text-orange-600 mt-1">Como administrador no tienes grupo asignado. Usa el Panel de Administración.</p>
          )}
        </div>

        {message && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm font-medium">
            {message}
          </div>
        )}

        {!categoria ? null : (
          <>
            {/* Tabs */}
            <div className="flex gap-2 mb-4 overflow-x-auto">
              <button
                onClick={() => setActiveTab('lista')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'lista' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300'
                }`}
              >
                📋 Pasar Lista
              </button>
              <button
                onClick={() => setActiveTab('jugadores')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'jugadores' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300'
                }`}
              >
                👦 Jugadores ({alumnos.length})
              </button>
              <button
                onClick={() => setActiveTab('historial')}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'historial' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300'
                }`}
              >
                📅 Historial
              </button>
            </div>

            {/* TAB: Pasar lista */}
            {activeTab === 'lista' && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-4 border-b flex flex-col sm:flex-row sm:items-center gap-3">
                  <label className="text-sm font-medium text-gray-700">Fecha:</label>
                  <input
                    type="date"
                    value={fechaLista}
                    onChange={(e) => setFechaLista(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-base"
                  />
                  <span className="text-sm text-gray-500">
                    {Object.values(asistenciaMap).filter(Boolean).length} de {alumnos.length} presentes
                  </span>
                </div>

                <div className="divide-y">
                  {alumnos.length === 0 && (
                    <p className="p-6 text-center text-gray-500">No hay niños en este grupo todavía</p>
                  )}
                  {alumnos.map(alumno => (
                    <div
                      key={alumno.id}
                      className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleAsistencia(alumno.id)}
                    >
                      <div className="flex items-center gap-3">
                        {alumno.foto_alumno ? (
                          <img
                            src={`/uploads/alumnos/${alumno.foto_alumno}`}
                            alt=""
                            className="h-10 w-10 rounded-full object-cover border"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">👦</div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{alumno.nombre} {alumno.apellidos}</p>
                          <p className="text-xs text-gray-500">{alumno.padre_telefono}</p>
                        </div>
                      </div>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold transition-colors ${
                        asistenciaMap[alumno.id]
                          ? 'bg-green-500 text-white'
                          : 'bg-red-100 text-red-500'
                      }`}>
                        {asistenciaMap[alumno.id] ? '✓' : '✗'}
                      </div>
                    </div>
                  ))}
                </div>

                {alumnos.length > 0 && (
                  <div className="p-4 border-t sticky bottom-0 bg-white">
                    <button
                      onClick={guardarLista}
                      disabled={guardandoLista}
                      className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-bold rounded-lg transition-colors"
                    >
                      {guardandoLista ? 'Guardando...' : `Guardar lista del ${formatFecha(fechaLista)}`}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* TAB: Jugadores */}
            {activeTab === 'jugadores' && (
              <div className="space-y-3">
                {alumnos.length === 0 && (
                  <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">No hay niños en este grupo todavía</div>
                )}
                {alumnos.map(alumno => (
                  <div key={alumno.id} className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        {alumno.foto_alumno ? (
                          <img
                            src={`/uploads/alumnos/${alumno.foto_alumno}`}
                            alt=""
                            className="h-12 w-12 rounded-full object-cover border"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xl">👦</div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-900">{alumno.nombre} {alumno.apellidos}</p>
                          <p className="text-xs text-gray-500">
                            Nacido: {new Date(alumno.fecha_nacimiento).toLocaleDateString('es-ES')}
                            {alumno.colegio && ` · ${alumno.colegio}`}
                          </p>
                          <p className="text-xs text-gray-500">
                            Tutor: {alumno.padre_nombre} {alumno.padre_apellidos} · <a href={`tel:${alumno.padre_telefono}`} className="text-blue-600">{alumno.padre_telefono}</a>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => abrirObservaciones(alumno)}
                        className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded-md text-xs font-medium hover:bg-amber-200"
                      >
                        📝 Observaciones
                      </button>
                      <select
                        value=""
                        onChange={(e) => moverJugador(alumno, e.target.value)}
                        className="px-2 py-1.5 border border-gray-300 rounded-md text-xs bg-white"
                      >
                        <option value="">⇄ Mover a otro grupo...</option>
                        {categorias
                          .filter(c => c.id !== alumno.categoria_id)
                          .map(c => (
                            <option key={c.id} value={c.id}>
                              {c.nombre}{c.limite_alumnos ? ` (${c.alumnos_inscritos}/${c.limite_alumnos})` : ''}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* TAB: Historial */}
            {activeTab === 'historial' && (
              <div className="bg-white rounded-lg shadow divide-y">
                {fechasHistorial.length === 0 && (
                  <p className="p-6 text-center text-gray-500">Todavía no se ha pasado lista ningún día</p>
                )}
                {fechasHistorial.map(f => (
                  <button
                    key={f.fecha}
                    onClick={() => { setFechaLista(f.fecha); setActiveTab('lista'); }}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 text-left"
                  >
                    <span className="font-medium text-gray-800">{formatFecha(f.fecha)}</span>
                    <span className="text-sm">
                      <span className="text-green-600 font-semibold">{f.asistieron}</span>
                      <span className="text-gray-400"> / {f.total} asistieron</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal observaciones */}
      {alumnoObs && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center" onClick={() => setAlumnoObs(null)}>
          <div
            className="bg-white w-full sm:max-w-lg sm:rounded-lg rounded-t-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Observaciones - {alumnoObs.nombre} {alumnoObs.apellidos}</h3>
              <button onClick={() => setAlumnoObs(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {observaciones.length === 0 && (
                <p className="text-center text-gray-500 text-sm py-4">Sin observaciones todavía</p>
              )}
              {observaciones.map(obs => (
                <div key={obs.id} className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-800">{obs.texto}</p>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-500">
                      {obs.autor} · {new Date(obs.created_at).toLocaleString('es-ES')}
                    </span>
                    <button
                      onClick={() => eliminarObservacion(obs.id)}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t flex gap-2">
              <input
                type="text"
                value={nuevaObs}
                onChange={(e) => setNuevaObs(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && guardarObservacion()}
                placeholder="Escribir observación..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={guardarObservacion}
                disabled={!nuevaObs.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-md font-medium"
              >
                Añadir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrupoView;
